import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// Side-effect import — wires @slumbr/core to localStorage / Vite env / etc.
// Must come before any other import that pulls @slumbr/core, since the
// theme store hydrates persisted state at module load.
import "./platform-adapter";

import { App } from "./app";
import { installServiceWorkerUpdater } from "./utils/sw-updater";
import {
  consumeSkipServiceWorker,
  guardedReload,
  purgeServiceWorker,
  reloadLoopTripped,
  markAppStable,
  resetApp,
} from "./utils/reload-guard";
import "./globals.css";

// Electron build has no service worker (file:// can't register one).
// A load that follows a chunk-recovery purge deliberately runs WITHOUT one: re-registering immediately
// handed the failing chunk request straight back to the worker that had just failed it, which is what made
// recovery loop. See consumeSkipServiceWorker.
if (import.meta.env.VITE_TARGET !== "electron") {
  if (consumeSkipServiceWorker()) console.warn("[sw] skipped for this load — recovering from a chunk failure");
  else installServiceWorkerUpdater();
}

// Deploy resilience: after a new deploy the previous build's lazy chunk hashes
// no longer exist, so a dynamic import can fail (404 / SPA-fallback HTML →
// "Failed to fetch dynamically imported module"). Vite fires `vite:preloadError`
// for exactly this. Recover by reloading once to pull the fresh index.html +
// current chunk hashes. A sessionStorage guard prevents an infinite reload loop
// if the failure is something other than a stale deploy.
window.addEventListener("vite:preloadError", () => {
  // PURGE FIRST, then reload. A chunk 404 after a deploy usually means a precached shell pinned to
  // hashes the server has purged, and that cannot be fixed by reloading: every reload is served by the
  // same worker and fails identically, so we used to spend the entire reload budget on doomed attempts
  // and land on the manual recovery screen. Clearing the worker makes the one reload that follows able
  // to succeed. The breaker still counts, so a chunk that genuinely cannot load stops after MAX_RELOADS
  // instead of purging in a loop.
  void purgeServiceWorker().finally(() => { guardedReload({ cacheBust: true }); });
});

// Tell the inline boot watchdog (index.html) the bundle is alive — it stops
// watching the moment real code runs, so it can never fight in-app recovery.
(window as unknown as { __slumbrBooted?: boolean }).__slumbrBooted = true;

const root = createRoot(document.getElementById("root")!);

if (reloadLoopTripped()) {
  // Too many reloads too fast — we're wedged (usually a stale service worker
  // pinning chunk hashes the server has purged). Stop refreshing; offer a
  // one-click hard reset that unregisters the SW and clears caches.
  root.render(<RecoveryScreen />);
} else {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  // App mounted — if it stays up, clear the rapid-reload tally.
  markAppStable();
}

function RecoveryScreen() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-[#0b0d0f] px-6 text-center text-[#e7e9ea]">
      <p className="text-base font-black">Slumbr got stuck updating</p>
      <p className="max-w-sm text-sm text-[#8b93c7]">
        An old cached version kept reloading. Reset clears it and loads the
        current build — your data is safe.
      </p>
      <button
        type="button"
        onClick={() => void resetApp()}
        className="rounded-full bg-[#c83050] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#d63b5d]"
      >
        Reset &amp; reload
      </button>
    </div>
  );
}
